import ResourceListing from '@efdigital/ll-addon-content/components/resource-listing';
import type { Resource } from '@efdigital/ll-addon-data';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

import { getPaginationTotal } from '../../helpers/get-pagination-total.ts';
import { PaginationByPageNumber } from '../pagination.gts';
import { resourceTemplateParts } from '../parts.gts';
import {
  SearchResources,
  SearchResourcesState,
} from '../search/search-resources.gts';

type Signature = {
  Args: {
    resourceId: string;
    onSelect: (resource: Resource) => void;
  };
  Blocks: {
    input: [showModal: () => void];
  };
};

const parts = resourceTemplateParts;

export default class ResourcePicker extends Component<Signature> {
  @tracked isOpen = false;
  @tracked state = new SearchResourcesState();
  @service declare store: Store;

  showModal = () => {
    this.isOpen = true;
  };

  modalClosed = () => {
    this.isOpen = false;
  };

  setState = (state: Partial<SearchResourcesState>) => {
    Object.assign(this.state, state);
  };

  setPageIndex = (pageIndex: number): void => {
    this.setState({ pageIndex });
  };

  selectResource = (resource: Resource, event: Event) => {
    event.preventDefault();
    this.args.onSelect(resource);
    this.isOpen = false;
  };

  <template>
    {{yield this.showModal to="input"}}
    <BsModal
      @open={{this.isOpen}}
      @onHidden={{this.modalClosed}}
      @size="xl"
      @renderInPlace={{true}}
      @backdropClose={{true}}
      as |modal|
    >
      <modal.body>
        <SearchResources
          @legend="Resources"
          @state={{this.state}}
          @setState={{this.setState}}
        >
          <:query as |query|>
            <Request @query={{query}}>
              <:content as |response|>
                {{#if response.data.length}}

                  {{#if response.meta.page}}
                    <PaginationByPageNumber
                      @pageIndex={{this.state.pageIndex}}
                      @pageSize={{this.state.limit}}
                      @total={{getPaginationTotal response}}
                      @onPageIndexChange={{this.setPageIndex}}
                      class="my-4 d-flex justify-content-center"
                      data-test-pagination-upper
                    />
                  {{/if}}

                  <div class="row my-4">
                    {{#each response.data as |resource|}}
                      <div class="col-md-4 pb-4">
                        <ResourceListing
                          @resource={{resource}}
                          @parts={{parts}}
                          @clicked={{this.selectResource}}
                        />
                      </div>
                    {{/each}}
                  </div>

                  {{#if response.meta.page}}
                    <PaginationByPageNumber
                      @pageIndex={{this.state.pageIndex}}
                      @pageSize={{this.state.limit}}
                      @total={{getPaginationTotal response}}
                      @onPageIndexChange={{this.setPageIndex}}
                      class="my-4 d-flex justify-content-center"
                      data-test-pagination-lower
                    />
                  {{/if}}
                {{else}}
                  <div
                    class="alert alert-info d-flex align-items-center my-4"
                    role="alert"
                  >
                    <span class="bi bi-info-circle me-2"></span>
                    <div>No resources found.</div>
                  </div>
                {{/if}}
              </:content>
              <:loading>
                <div class="text-center my-5">
                  <div class="spinner-grow" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
              </:loading>
            </Request>
          </:query>
        </SearchResources>
      </modal.body>
    </BsModal>
  </template>
}
